import React from 'react';
import classnames from 'classnames';
import type { DraftContent } from 'ricos-content';
import { Link_Target, type RichContent } from 'ricos-schema';
import { useTranslation } from '@wix/yoshi-flow-editor';
import {
  resolveId,
  isRichContent,
  isExperimentEnabled,
  EXPERIMENTS,
} from '@wix/communities-blog-client-common';
// @ts-expect-error
import { PaywallBanner } from '@wix/communities-blog-client-common/dist/esm/components';
import RichContentViewer from '../../../rich-content/components/rich-content-viewer';
import useFontClassName from '../../hooks/use-font-class-name';
import { getAppSettings } from '../../selectors/app-settings-base-selectors';
import { getSection } from '../../selectors/section-selectors';
import { getPaywallBanner } from '../../store/paywall/paywall-selectors';
import { getTopology } from '../../store/topology/topology-selectors';
import type { NormalizedPost } from '../../types';
import { useSelector, useActions } from '../runtime-context';
import styles from './post-content.scss';

type PaywallBannerWithLinkProps = {
  paywallContent: RichContent | DraftContent;
};

const PaywallBannerWithLink: React.FC<PaywallBannerWithLinkProps> = ({
  paywallContent,
}) => {
  const isDraftJS = !isRichContent(paywallContent);
  const appSettings = useSelector(getAppSettings);
  const section = useSelector(getSection);
  const pricingPlansUrl = useSelector((state) => state.pricingPlansUrl);
  const { baseUrl } = useSelector(getTopology);
  const subscribeURL = React.useMemo(() => {
    if (baseUrl && pricingPlansUrl) {
      return new URL(baseUrl + pricingPlansUrl).href;
    }
  }, [baseUrl, pricingPlansUrl]);

  const modifiedPaywallContent = React.useMemo(() => {
    try {
      if (isDraftJS) {
        const stringifiedContent = JSON.stringify(paywallContent);
        return JSON.parse(
          stringifiedContent
            .replace('"url":""', `"url": "${subscribeURL}"`)
            .replace('"target":true', '"target":"SELF"')
            .replace(
              'wix-draft-plugin-action-button',
              'wix-draft-plugin-link-button',
            ),
        );
      } else if ('nodes' in paywallContent) {
        const buttonNode = paywallContent.nodes[2];

        if (
          buttonNode.buttonData &&
          buttonNode.buttonData?.link === undefined
        ) {
          const link = {
            url: subscribeURL,
            anchor: undefined,
            target: Link_Target.SELF,
            rel: {
              nofollow: true,
              sponsored: false,
            },
          };

          buttonNode.buttonData.link = link;
        }

        return paywallContent;
      }
    } catch {
      return paywallContent;
    }
  }, [isDraftJS, paywallContent, subscribeURL]);

  return (
    <PaywallBanner
      banner={modifiedPaywallContent}
      viewer={RichContentViewer}
      appSettings={appSettings}
      section={section}
      className={styles.banner}
    />
  );
};

type PostContentProps = {
  post: NormalizedPost;
};

export const PostContent: React.FC<PostContentProps> = ({ post }) => {
  const appSettings = useSelector(getAppSettings);
  const section = useSelector(getSection);
  const banner = useSelector(getPaywallBanner);
  const actions = useActions();
  const useLinkForSubscribeNow = useSelector((state) =>
    isExperimentEnabled(
      state,
      EXPERIMENTS.USE_LINK_FOR_PAYWALL_SUBSCRIBE_NOW_BUTTON,
    ),
  );

  const { contentFontClassName } = useFontClassName();

  const paywallContent =
    banner ?? post?.paywallRichContent ?? post?.paywallContent;

  const { t } = useTranslation();

  const postContentBodyClass = 'post-content__body';

  return (
    <article className={contentFontClassName}>
      <div className={classnames(postContentBodyClass, styles.rceNextView)}>
        {typeof post.content === 'object' && (
          <RichContentViewer
            initialRawState={post.content}
            post={post}
            parentClass={postContentBodyClass}
          />
        )}
      </div>
      {post?.canSeePaidContent === false && paywallContent && (
        <div className={styles.paywallContainer}>
          {useLinkForSubscribeNow ? (
            <PaywallBannerWithLink paywallContent={paywallContent} />
          ) : (
            <PaywallBanner
              banner={paywallContent}
              viewer={RichContentViewer}
              appSettings={appSettings}
              section={section}
              className={styles.banner}
              onSubscribe={() =>
                actions.navigateToPaidPlans(
                  resolveId(post),
                  post.paidPlansGroupIds,
                  post.slug,
                  t('subscription.thanks-page.title'),
                  t('subscription.thanks-page.text'),
                  t('subscription.thanks-page.continue-button'),
                )
              }
            />
          )}
        </div>
      )}
    </article>
  );
};

export default PostContent;
